/**
 * Shared chrome for simple turnover steps — Back + tappable progress.
 */
import { useEffect, useState, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { useActiveJobClock } from "@/lib/activeJobClock";
import { OfflineLine } from "@/components/cleaner/OfflineLine";
import { formatClockTime, formatRunningShort, minutesBetween } from "@/lib/locationStamps";
import { SimpleFlowProgress } from "@/components/cleaner/SimpleFlowProgress";
import { useTranslation } from "@/hooks/useTranslation";

export function SimpleStepHeader({
  title,
  activeSegment,
  onBack,
  onSelectSegment,
  right,
  subtitle,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
}: {
  title: string;
  activeSegment: number;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  right?: ReactNode;
  subtitle?: ReactNode;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
}) {
  const { t } = useTranslation();
  const clock = useActiveJobClock();
  const [nowIso, setNowIso] = useState(() => new Date().toISOString());
  useEffect(() => {
    if (!clock.startedAt) return;
    setNowIso(new Date().toISOString());
    const id = window.setInterval(() => setNowIso(new Date().toISOString()), 30_000);
    return () => window.clearInterval(id);
  }, [clock.startedAt]);
  const running = clock.startedAt ? minutesBetween(clock.startedAt, nowIso) : null;
  return (
    <div className="flex shrink-0 flex-col gap-3 px-5 pb-3 pt-1">
      {clock.startedAt ? (
        <div className="flex items-baseline justify-between gap-2.5 px-0.5">
          <div className="text-[17px] font-bold text-[#0f1720]">
            {t("jobStartedAt").replace("{{time}}", formatClockTime(clock.startedAt))}
          </div>
          <div className="text-[17px] font-bold text-[#15776d]">
            {formatRunningShort(running ?? 0)}
          </div>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[#f4f7f9] text-[#475569] active:scale-95"
            aria-label={t("back")}
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.6} />
          </button>
        ) : null}
        <div className="min-w-0 flex-1 text-[30px] font-bold tracking-[-0.6px] text-[#0f1720]">
          {title}
        </div>
        {right}
      </div>
      <SimpleFlowProgress
        active={activeSegment}
        onSelect={onSelectSegment}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
      />
      <OfflineLine />
      {clock.locationNotSaved && activeSegment === 0 ? (
        <div className="flex items-center gap-2.5 rounded-[14px] border border-[#e6edf1] bg-white px-3.5 py-3">
          <div className="h-2.5 w-2.5 shrink-0 rounded-full border-2 border-[#94a3b0]" aria-hidden />
          <div className="min-w-0 flex-1 text-[14px] font-medium text-[#64748b]">{t("locationNotSavedLine")}</div>
        </div>
      ) : null}
      {subtitle}
    </div>
  );
}
